<script setup lang="ts">
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { FileSpreadsheet, Gift, MessageSquare, Table2 } from "lucide-vue-next";
  import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { RadioGroup, RadioGroupItemIcon } from "@/components/ui/radio-group";
  import { Select } from "@/components/episto-ui/select";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useCurrentUserStore } from "@stores/currentUser";
  import { INDIVIDUAL_FORMATS, INDIVIDUAL_FORMAT_LABELS } from "@stores/survey/exports";
  import type { ExportFileFormat, ExportType, SurveyExportPayload } from "@/types";

  const form = defineModel<SurveyExportPayload>("form", { required: true });

  defineProps<{
    exportType: ExportType;
    individualFormat: ExportFileFormat;
    showMode: boolean;
    showFormat: boolean;
    weightsEnabled: boolean;
    autoName: string;
  }>();

  const emit = defineEmits<{
    "change-type": [value: ExportType];
    "change-format": [value: ExportFileFormat];
    "change-admin-mode": [value: boolean];
  }>();

  const { survey } = storeToRefs(useSurveyStore());
  const { isAdmin } = storeToRefs(useCurrentUserStore());
  const hasAiChat = computed(() => survey.value?.ai_chat === true);

  const typeOptions = computed(() => [
    { value: "individual", label: "Individual responses", description: "One row per respondent", icon: FileSpreadsheet },
    { value: "tables", label: "Tables & Crosstabs", description: "Aggregated counts and percentages", icon: Table2 },
    ...(hasAiChat.value
      ? [{ value: "ai_chat", label: "AI conversations", description: "Interview and assistant transcripts, with summaries", icon: MessageSquare }]
      : []),
    ...(isAdmin.value
      ? [{ value: "rewards", label: "Reward payouts", description: "Tremendous upload files", icon: Gift }]
      : []),
  ]);
  const individualFormatOptions = INDIVIDUAL_FORMATS.map((value) => ({ value, label: INDIVIDUAL_FORMAT_LABELS[value] }));
  // `Select` emits strings and reads `false` as "no selection", so boolean fields use string options.
  const modeOptions = [
    { value: "client", label: "Client" },
    { value: "admin", label: "Admin" },
  ];
  const weightedOptions = [
    { value: "raw", label: "Raw" },
    { value: "weighted", label: "Weighted" },
  ];
</script>

<template>
  <AccordionItem value="general">
    <AccordionTrigger class="text-base font-semibold">General</AccordionTrigger>
    <AccordionContent>
      <div class="space-y-6 pt-2">
        <div
          v-if="showMode"
          class="space-y-2">
          <Label>Mode</Label>
          <Select
            :model-value="form.admin ? 'admin' : 'client'"
            :options="modeOptions"
            class="w-full"
            @update:model-value="emit('change-admin-mode', $event === 'admin')" />
        </div>
        <div class="space-y-2">
          <Label>Export name</Label>
          <Input
            v-model="form.name"
            :placeholder="autoName" />
        </div>
        <div
          v-if="weightsEnabled"
          class="space-y-2">
          <Label>Raw / weighted</Label>
          <Select
            :model-value="form.weighted_data ? 'weighted' : 'raw'"
            :options="weightedOptions"
            class="w-full"
            @update:model-value="form.weighted_data = $event === 'weighted'" />
        </div>
        <div class="space-y-2">
          <Label>Type</Label>
          <RadioGroup
            :model-value="exportType"
            class="grid grid-cols-3 gap-3 pt-1"
            @update:model-value="emit('change-type', String($event) as ExportType)">
            <RadioGroupItemIcon
              v-for="option in typeOptions"
              :key="option.value"
              :value="option.value"
              :label="option.label"
              :description="option.description">
              <template #icon>
                <component :is="option.icon" />
              </template>
            </RadioGroupItemIcon>
          </RadioGroup>
        </div>
        <div
          v-if="showFormat"
          class="space-y-2">
          <Label>Format</Label>
          <Select
            :model-value="individualFormat"
            :options="individualFormatOptions"
            class="w-full"
            @update:model-value="emit('change-format', $event as ExportFileFormat)" />
        </div>
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
